import React, { useState, useEffect } from 'react';
import { UserIcon, UsersIcon, FileTextIcon, FolderIcon, XIcon } from 'lucide-react';

/**
 * UserProfile component - shows user's bands, sheets, and setlists
 * 
 * @param {Object} props - Component props
 * @param {string} props.userId - User ID to display
 * @param {string} props.username - Username to display
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 */
const UserProfile = ({ userId, username, isOpen, onClose }) => {
  const [userBands, setUserBands] = useState([]);
  const [userSheets, setUserSheets] = useState([]);
  const [userSetlists, setUserSetlists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('bands');

  useEffect(() => {
    if (!isOpen || !userId) return;

    const fetchUserData = async () => {
      setLoading(true);
      try {
        // Fetch user's public bands
        const bandsResponse = await fetch(`/api/users/${userId}/bands`, {
          credentials: 'include'
        });
        if (bandsResponse.ok) {
          const bandsResult = await bandsResponse.json();
          setUserBands(bandsResult.data || []);
        }

        // Fetch user's public sheets
        const sheetsResponse = await fetch(`/api/users/${userId}/sheets`, {
          credentials: 'include'
        });
        if (sheetsResponse.ok) {
          const sheetsResult = await sheetsResponse.json();
          setUserSheets(sheetsResult.data || []);
        }

        // Fetch user's public setlists
        const setlistsResponse = await fetch(`/api/users/${userId}/setlists`, {
          credentials: 'include'
        });
        if (setlistsResponse.ok) {
          const setlistsResult = await setlistsResponse.json();
          setUserSetlists(setlistsResult.data || []);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [isOpen, userId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white">
              <UserIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">{username}</h2>
              <p className="text-gray-500 text-sm">Music Profile</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('bands')}
            className={`px-6 py-3 font-medium border-b-2 ${
              activeTab === 'bands'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <UsersIcon className="w-4 h-4" />
              Bands ({userBands.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('sheets')}
            className={`px-6 py-3 font-medium border-b-2 ${
              activeTab === 'sheets'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileTextIcon className="w-4 h-4" />
              Sheets ({userSheets.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('setlists')}
            className={`px-6 py-3 font-medium border-b-2 ${
              activeTab === 'setlists'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <FolderIcon className="w-4 h-4" />
              Setlists ({userSetlists.length})
            </div>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : (
            <>
              {activeTab === 'bands' && (
                <div className="space-y-3">
                  {userBands.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No public bands</p>
                  ) : (
                    userBands.map(band => (
                      <div key={band._id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <UsersIcon className="w-5 h-5 text-gray-500" />
                            <div>
                              <h3 className="font-medium">{band.name}</h3>
                              <p className="text-sm text-gray-500">{band.genre}</p>
                            </div>
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            <div>{band.stats?.member_count || 0} members</div>
                            <div>{band.stats?.setlist_count || 0} setlists</div>
                          </div>
                        </div>
                        {band.description && (
                          <p className="text-sm text-gray-600 mt-2">{band.description}</p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'sheets' && (
                <div className="space-y-3">
                  {userSheets.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No public sheets</p>
                  ) : (
                    userSheets.map(sheet => (
                      <div key={sheet._id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileTextIcon className="w-5 h-5 text-gray-500" />
                            <div>
                              <h3 className="font-medium">{sheet.title}</h3>
                              <p className="text-sm text-gray-500">{sheet.artist}</p>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {sheet.status === 'Ready' ? '✓ Ready' : '⚠ WIP'}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'setlists' && (
                <div className="space-y-3">
                  {userSetlists.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No public setlists</p>
                  ) : (
                    userSetlists.map(setlist => (
                      <div key={setlist._id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FolderIcon className="w-5 h-5 text-gray-500" />
                            <div>
                              <h3 className="font-medium">{setlist.name}</h3>
                              <p className="text-sm text-gray-500">
                                {setlist.sheets?.length || 0} sheets
                              </p>
                            </div>
                          </div>
                          {setlist.band_id && (
                            <div className="text-sm text-gray-500">
                              Band setlist
                            </div>
                          )}
                        </div>
                        {setlist.description && (
                          <p className="text-sm text-gray-600 mt-2 [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800" dangerouslySetInnerHTML={{ __html: setlist.description }}></p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;